/**
 * The render stage: renderer, scene, camera and everything the world contains, plus `apply(frame)` which
 * poses it all from a FrameState. Built in the concept's order (concept L642–L762) so the world-generation
 * stream is consumed identically. M1 grows this task by task (docs/plans/2026-09-26-m1-world-visuals.md).
 */
import { Fog, PerspectiveCamera, Scene, Vector3, type Mesh, type WebGLRenderer } from 'three';
import type { FrameState } from './frame.ts';
import { createRenderer } from './renderer.ts';
import { mulberry32 } from '../core/rng.ts';
import { createLights, type Lights } from '../world/lighting.ts';
import { LOOKS } from '../world/looks.ts';
import { MODES, type Fidelity, type ModeName } from '../world/modes.ts';
import { WORLDS } from '../world/profiles.ts';
import { bindEnvironment, makeSky, skyEnvironment } from '../world/sky.ts';
import { createTextures, type TextureSet } from '../world/textures.ts';
import { buildStaticWorld, type StaticWorld } from '../world/world.ts';
import { LAYER, worldSettings, type WorldSettings } from '../world/fidelity.ts';
import { WORLD_Z0, worldZ1 } from '../world/water.ts';
import { poseTextures } from '../world/textureMotion.ts';
import { createEntityViews, type EntityViews } from '../entities/views.ts';
import { createParticleSystems, type ParticleSystems } from '../effects/particles.ts';
import { buildClouds, buildSkyLife, type Atmosphere } from '../world/atmosphere.ts';
import { SpeedLines } from '../effects/speedLines.ts';
import { Vector2 } from 'three';

export interface StageOptions {
  canvas: HTMLCanvasElement;
  mode: ModeName;
  width: number;
  height: number;
  fidelity: Fidelity;
}

export interface Stage {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly world: StaticWorld;
  readonly lights: Lights;
  readonly sky: Mesh;
  readonly textures: TextureSet;
  readonly settings: WorldSettings;
  readonly views: EntityViews;
  readonly particles: ParticleSystems;
  readonly atmosphere: Atmosphere | null;
  /** Chase camera only (v1.1): streaks attached to the camera. */
  readonly speedLines: SpeedLines | null;
  apply(frame: FrameState): void;
  render(): void;
  dispose(): void;
}

export async function createStage(opts: StageOptions): Promise<Stage> {
  const cfg = MODES[opts.mode];
  const look = LOOKS[cfg.look];
  const W = WORLDS[cfg.world];
  const renderer = createRenderer(opts.canvas, opts.width, opts.height, look.exposure);

  // World generation stream (concept L654), consumed in the concept's order by everything built below.
  const rand = mulberry32(opts.mode === 'canyon' ? 77 : 41);
  const scene = new Scene();
  scene.fog = new Fog(look.fog, look.fogNear, look.fogFar);
  const camera = new PerspectiveCamera(cfg.fov, opts.width / opts.height, 0.5, 4000); // concept L657
  scene.add(camera);
  const sunDir = new Vector3().fromArray(look.sunDir).normalize();
  const sky = makeSky(look, sunDir);
  scene.add(sky);
  scene.environment = skyEnvironment(renderer, look, sunDir);
  const settings = worldSettings(cfg, opts.fidelity, WORLD_Z0, worldZ1(cfg));
  const lights = createLights(scene, look, settings.shadowHalfExtent, settings.shadowMapSize);

  const textures = createTextures();
  const world = buildStaticWorld(scene, W, cfg, look, textures, rand, settings);
  const topCamera = cfg.cam === 'top';
  const clouds = topCamera ? buildClouds(scene, W, rand, textures.cloud) : [];
  const views = createEntityViews(scene, W, look, textures);
  const particles = createParticleSystems(scene, textures.dot, textures.smoke);
  if (settings.stableLayers) for (const p of Object.values(particles)) p.points.renderOrder = LAYER.particles;
  // Speed lines draw from the world stream right after the particles, as in the concept (L847–L853).
  const speedLines = cfg.cam === 'chase' ? new SpeedLines(rand) : null;
  if (speedLines) camera.add(speedLines.lines);
  // The jet starts 20 m upriver on the centerline (concept L857); sky life is placed around it.
  const atmosphere = topCamera
    ? buildSkyLife(scene, W, rand, textures.cloud, { x: W.center(-20), z: -20 }, clouds)
    : null;
  const drawingBuffer = new Vector2();
  bindEnvironment(scene, scene.environment);

  return {
    renderer,
    scene,
    camera,
    world,
    lights,
    sky,
    textures,
    settings,
    views,
    particles,
    atmosphere,
    speedLines,
    apply(frame) {
      camera.position.fromArray(frame.camera.pos);
      camera.quaternion.fromArray(frame.camera.quat);
      camera.fov = frame.camera.fov;
      camera.aspect = frame.camera.aspect;
      camera.updateProjectionMatrix();
      sky.position.copy(camera.position);
      lights.sun.position.fromArray(frame.sun.pos);
      lights.sun.target.position.fromArray(frame.sun.target);
      renderer.toneMappingExposure = frame.exposure;
      // Concept fidelity draws the recorded offsets; the GDD's water scrolls downstream (D3), from the same clock.
      if (settings.waterScroll === 'upriver') {
        textures.n1.offset.fromArray(frame.tex.n1);
        textures.n2.offset.fromArray(frame.tex.n2);
        textures.foam.offset.fromArray(frame.tex.foam);
      } else poseTextures(textures, world.islandFoamMap, frame.clock, settings.waterScroll, 0);
      views.apply(frame);
      atmosphere?.apply(frame);
      renderer.getDrawingBufferSize(drawingBuffer);
      for (const [name, p] of Object.entries(particles) as [
        keyof ParticleSystems,
        ParticleSystems[keyof ParticleSystems],
      ][]) {
        p.setScale(drawingBuffer.y, camera.fov);
        p.inject(frame.particles[name]);
      }
    },
    render() {
      renderer.render(scene, camera);
    },
    dispose() {
      renderer.dispose();
    },
  };
}
